import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { useAppSelector } from 'redux/storeHooks';
import { SurveyCreatorState } from 'redux/types/surveyCreatorState.types';
import { surveyAdminRequests } from 'redux/requests/survey-admin.requests';
import { createApiThunk } from 'redux/utils/apiThunk';
import { SurveyAdmin, SurveyV2 } from '@act/shared/data-transfer-objects';

// THUNKS/ACTIONS ==============================================================
export const createSurveyCreatorRecord = createApiThunk(
  surveyAdminRequests.createSurveyCreatorRecord
);
export const deleteSurveyCreatorRecord = createApiThunk(
  surveyAdminRequests.deleteSurveyCreatorRecord
);
export const updateSurveyCreatorRecord = createApiThunk(
  surveyAdminRequests.updateSurveyCreatorRecord
);
export const getSurveyRecordById = createApiThunk(
  surveyAdminRequests.getSurveyRecordById
);
export const getSurveyRecords = createApiThunk(
  surveyAdminRequests.getSurveyRecords
);

// INITIAL STATE ===============================================================
const surveyCreatorRecordAdapter =
  createEntityAdapter<SurveyAdmin.SurveyCreatorRecordDto>();

const initialState: SurveyCreatorState = {
  status: 'initial',
  currentContent: SurveyV2.DEFAULT_SURVEY_CONTENT,
  records: surveyCreatorRecordAdapter.getInitialState(),
};

// SLICE =======================================================================
export const surveyCreatorSlice = createSlice({
  name: 'surveyCreator',
  initialState,
  reducers: {
    setCurrentSurveyContent: (
      state,
      action: PayloadAction<SurveyV2.CreateSurveyJSONDto & { id?: number }>
    ) => {
      state.currentContent = action.payload;
    },
    setCurrentSurveyContentById: (state, action: PayloadAction<number>) => {
      const record = state.records.entities[action.payload];
      if (record) {
        state.currentContent = {
          id: action.payload,
          key: record.key,
          surveyContent: record.surveyContent,
          selectedSurveyCreatorRecordId: action.payload,
          selectedLearnMoreRecordId: -1,
        };
      }
    },
    setQuestions: (
      state,
      action: PayloadAction<SurveyV2.CreateQuestionJSONDto[]>
    ) => {
      state.currentContent.surveyContent.surveyVersion.questions =
        action.payload;
    },
    resetCurrentSurveyContent: state => {
      state.currentContent = SurveyV2.DEFAULT_SURVEY_CONTENT;
    },
    updateQuestionOptions: (
      state,
      action: PayloadAction<{
        questionKey: string;
        options: SurveyV2.CreateOptionJSONDto[];
      }>
    ) => {
      const { questionKey, options } = action.payload;
      const question =
        state.currentContent.surveyContent.surveyVersion.questions.find(
          q => q.key === questionKey
        );
      if (question) {
        question.options = options;
      }
    },
    upsertSurveyRecord: (
      state,
      action: PayloadAction<SurveyAdmin.SurveyCreatorRecordDto>
    ) => {
      surveyCreatorRecordAdapter.upsertOne(state.records, action.payload);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getSurveyRecords.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        getSurveyRecords.fulfilled,
        (
          state,
          action: PayloadAction<SurveyAdmin.SurveyCreatorRecordDto[]>
        ) => {
          state.status = 'success';
          surveyCreatorRecordAdapter.upsertMany(state.records, action.payload);
        }
      )
      .addCase(getSurveyRecords.rejected, state => {
        state.status = 'failed';
      });
  },
});

// REDUCER =====================================================================
export const surveyCreatorReducer = surveyCreatorSlice.reducer;
export const {
  setCurrentSurveyContent,
  setCurrentSurveyContentById,
  setQuestions,
  resetCurrentSurveyContent,
  updateQuestionOptions,
  upsertSurveyRecord,
} = surveyCreatorSlice.actions;

// SELECTORS ===================================================================
export const selectSurveyCreatorState = state =>
  (state as { surveyCreator: typeof initialState })?.surveyCreator;

export const selectSurveyCreatorStateStatus = state =>
  (state as { surveyCreator: typeof initialState })?.surveyCreator?.status;

export const selectSurveyCreatorCurrentContent = state =>
  (state as { surveyCreator: typeof initialState })?.surveyCreator
    ?.currentContent;

export const surveyCreatorRecordSelectors =
  surveyCreatorRecordAdapter.getSelectors<SurveyCreatorState>(
    state => state.records
  );

export const selectAllSurveyCreatorRecords = createSelector(
  selectSurveyCreatorState,
  surveyCreatorRecordSelectors.selectAll
);

// EXPORT HELPER ===============================================================
export const surveyCreatorSelectors = {
  useSelectSurveyCreatorStatus: () =>
    useAppSelector(selectSurveyCreatorStateStatus),
  useSelectSurveyCreatorCurrentContent: () =>
    useAppSelector(selectSurveyCreatorCurrentContent),
  useSelectSurveyCreatorRecords: () =>
    useAppSelector(selectAllSurveyCreatorRecords),
};

// EFFECTS =====================================================================

// WEBSOCKET ACTIONS ===========================================================
